// src/Question.js

import React, { useState, useEffect, useCallback } from 'react';
import { Table, Spin, Alert } from 'antd';
import { useNavigate } from 'react-router-dom';
import { db } from './Firebase';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore'; 

import './Question.css'; 

function Question() {
  const navigate = useNavigate();
  const [usersData, setUsersData] = useState([]);
  const [loading, setLoading] = useState(true); // (ยังคงใช้สำหรับการโหลดครั้งแรก)
  const [error, setError] = useState(null);
  const [title, setTitle] = useState('คำตอบผู้เข้าแข่งขัน');

  // [แก้ไข] 1. ปรับ useCallback ให้รับ isBackgroundUpdate
  const fetchQuestionData = useCallback(async (isBackgroundUpdate = false) => {
    if (!db) return;
    
    // (ถ้าไม่ใช่ background update (เช่น โหลดครั้งแรก) ให้โชว์ Loading)
    if (!isBackgroundUpdate) {
      setLoading(true);
    }
    // (เราจะไม่ set error เป็น null ใน background update
    // เพื่อไม่ให้ Alert หายไปถ้าอัปเดตใหม่แล้วยัง error)
    // setError(null); 

    try {
      // --- 1. หาชื่อห้อง (จาก .../Admin/Room) ---
      const roomConfigRef = doc(db, "Competition-Training", "nathadaj", "Admin", "Room");
      const roomConfigSnap = await getDoc(roomConfigRef);

      if (!roomConfigSnap.exists()) {
        throw new Error("ไม่พบเอกสารตั้งค่าห้อง (Admin/Room)");
      }
      
      const roomName = roomConfigSnap.data().Meeting; 
      if (!roomName) {
        throw new Error("ไม่พบชื่อห้องในเอกสารตั้งค่า (field 'Meeting')");
      }

      // --- 2. ดึง Title (เหมือนใน Leaderboard) ---
      // (การดึง Title ทำแค่ครั้งแรกก็พอ ไม่ต้องทำทุก 5 วิ)
      if (!isBackgroundUpdate) {
        try {
          const roomInfoRef = doc(db, "Competition-Training", "nathadaj", roomName, "Admin");
          const roomInfoSnap = await getDoc(roomInfoRef);
          if (roomInfoSnap.exists() && roomInfoSnap.data().description) {
            setTitle(roomInfoSnap.data().description); // (ใช้ Title เดียวกัน)
          }
        } catch (titleError) {
           console.warn("Could not fetch title:", titleError.message);
        }
      }

      // --- 3. ดึงข้อมูลผู้ใช้ และคำตอบ 1-20 ---
      const roomUsersCollectionRef = collection(db, "Competition-Training", "nathadaj", roomName);
      const roomUsersSnapshot = await getDocs(roomUsersCollectionRef);

      if (roomUsersSnapshot.empty) {
         throw new Error(`ไม่พบผู้ใช้ในห้อง ${roomName}`);
      }

      const fetchedUsers = [];
      
      roomUsersSnapshot.forEach((userDoc) => {
          const username = userDoc.id;
          const data = userDoc.data();
          
          if (data.hide) {
              return; 
          }

          const fullName = (data.name && data.surname) 
              ? `${data.name} ${data.surname}` 
              : data.name || data.surname || username;
          
          const answers = {};
          for (let i = 1; i <= 20; i++) {
              const answerValue = data[`answer-${i}`];
              answers[i] = (answerValue === undefined || answerValue === null) ? '-' : String(answerValue);
          }

          fetchedUsers.push({
              id: username,
              name: fullName, 
              answers: answers, 
          });
      });

      setUsersData(fetchedUsers);
      setError(null); // (ถ้าดึงข้อมูลสำเร็จ ให้ล้าง Error เก่า)

    } catch (err) {
      if (err.code === 'permission-denied') {
        setError("Permission Denied: ตรวจสอบ Firestore Security Rules");
      } else {
        setError(`เกิดข้อผิดพลาด: ${err.message}`);
      }
      // (ถ้า error เราไม่เคลียร์ข้อมูลเก่า ให้แสดงข้อมูลล่าสุดที่เคยดึงได้)
      // setUsersData([]); 
    } finally {
      // (ปิด Loading เฉพาะการโหลดครั้งแรก)
      if (!isBackgroundUpdate) {
        setLoading(false);
      }
    }
  }, [db]); // (dependency ยังคงเป็น db)

  // [แก้ไข] 2. ปรับ useEffect ให้ใช้ setInterval
  useEffect(() => {
    if (db) {
      // 1. เรียกทันที 1 ครั้งสำหรับการโหลดหน้า
      fetchQuestionData(false); 

      // 2. ตั้ง Interval ให้เรียกซ้ำทุก 5 วินาที
      const intervalId = setInterval(() => {
        fetchQuestionData(true); // (true = เป็น background update)
      }, 5000); // 5000ms = 5 วินาที

      // 3. Cleanup function: จะถูกเรียกเมื่อ Component ถูก unmount
      return () => {
        clearInterval(intervalId); // หยุดการเรียกซ้ำ
      };
    }
  }, [db, fetchQuestionData]);

  // ฟังก์ชันสำหรับปุ่มกลับ
  const handleBackToLeaderboard = () => {
    navigate('/'); // กลับไปหน้าหลัก (Leaderboard)
  };

  // --- สร้าง Columns สำหรับตาราง ---
  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      fixed: 'left', 
      width: 180, 
      className: 'name-cell-question',
    },
  ];

  for (let i = 1; i <= 20; i++) {
    columns.push({
      title: `ข้อ ${i}`, 
      dataIndex: 'answers',
      key: `ans-${i}`,
      align: 'center',
      width: 80, 
      render: (answers) => answers[i], 
    });
  }
  // ----------------------------------------

  return (
    <div className="question-container"> 
      <div className="question-header">
        <div className="logo-container">
          <img 
            src="/Wisdom-Logo.png" 
            alt="Wisdom Logo" 
            className="logo-image1"
            onClick={() => fetchQuestionData(false)} // (คลิกโลโก้ = refresh โชว์ Loading)
          />
        </div>
        
        <h1 className="question-title">
          {title}
        </h1>

        <div className="button-group">
          <button onClick={handleBackToLeaderboard} className="back-button-question">
            กลับ
          </button>        
        </div>
      </div>

      {error && (
        <Alert
          message="ข้อผิดพลาด"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: '20px' }}
        />
      )}

      {/* [แก้ไข] 3. ให้ Spin ทำงานเฉพาะเมื่อ loading (ครั้งแรก) 
          และ ตารางจะไม่หายไปตอนอัปเดต background */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <p>กำลังโหลดข้อมูลคำตอบ...</p>
        </div>
      ) : (
        <Table
          columns={columns}
          dataSource={usersData}
          rowKey="id"
          pagination={false}
          bordered
          scroll={{ x: 1800 }} 
          locale={{ emptyText: 'ไม่มีข้อมูล' }}
          className="question-table"
        />
      )}
    </div>
  );
}

export default Question;